import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { GroupView } from '@app/views';
import { useAdminEngagementReportViewCreator } from '@app/view-creator/AdminCohortEngagementReportCardViewCreator';

interface EngagementReportDialogProps {
  open: boolean;
  group: GroupView;
  onClose: () => void;
}

const getCellBgColor = (progressCount: number, goalCount: number) => {
  if (goalCount === 0) return '#f44336';
  if (progressCount >= goalCount) return '#4caf50';
  if (progressCount > 0) return '#ffeb3b';
  return '#f44336';
};

const getCellColor = (progressCount: number, goalCount: number) => {
  if (goalCount === 0) return '#fff';
  if (progressCount >= goalCount) return '#fff';
  if (progressCount > 0) return '#000';
  return '#fff';
};

export const EngagementReportDialog: React.FC<EngagementReportDialogProps> = ({ open, group, onClose }) => {
  const { data: engagementData, isLoading } = useAdminEngagementReportViewCreator(group.id, open);

  const maxWeeks = engagementData?.cohortUsersWeeklyData[0]?.weeks.length || 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          margin: '50px',
          width: 'calc(100vw - 100px)',
          height: 'calc(100vh - 100px)',
          maxWidth: 'none',
          maxHeight: 'none',
          fontFamily: 'Roboto, sans-serif',
        },
      }}
    >
      <DialogTitle>
        Engagement Report
        <Box sx={{ fontSize: '0.9em', mt: 1 }}>
          {group.name} - Week {engagementData?.currentWeekNumber}
        </Box>
      </DialogTitle>{' '}
      <DialogContent style={{ paddingTop: 4 }}>
        {engagementData ? (
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Roboto, sans-serif' }}>User</TableCell>
                {Array.from({ length: maxWeeks }, (_, i) => (
                  <TableCell key={i} align="center" sx={{ fontWeight: 'bold', fontFamily: 'Roboto, sans-serif' }}>
                    Week {i + 1}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {engagementData.cohortUsersWeeklyData.map((userData) => (
                <TableRow key={userData.cohortUser.id}>
                  <TableCell sx={{ fontFamily: 'Roboto, sans-serif' }}>
                    {userData.cohortUser.user.name || 'Unknown'}
                  </TableCell>{' '}
                  {userData.weeks.map((week) => (
                    <TableCell
                      key={week.week}
                      align="center"
                      sx={{
                        backgroundColor: getCellBgColor(week.progressCount, week.goalCount),
                        color: getCellColor(week.progressCount, week.goalCount),
                        fontFamily: 'Roboto, sans-serif',
                      }}
                    >
                      {week.progressCount} / {week.goalCount}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : isLoading ? (
          <Typography>Loading...</Typography>
        ) : (
          <Typography>No data available</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
